import { Injectable } from '@angular/core';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions as UiNotificationActions } from '@next/core-lib/notification';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';

import * as TGAVarietiesActions from './tga-varieties.actions';
import { selectCropVarieties, selectDerivedCropVarieties } from './tga-varieties.selectors';
import { VarietyPropertyDialogComponent, VarietyPropertyDialogData } from '../components/variety-property-dialog/variety-property-dialog.component';
import { TGAVarietiesService } from '../services/tga-varieties.service';

@Injectable()
export class TGAVarietiesEffects {
  loadTGAVarietyProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TGAVarietiesActions.loadTGAVarietyPropertiesRequest),
      switchMap(() =>
        this.tgaVarietiesService.getVarietyPropertiesTGA().pipe(
          map((varietyProperties) => TGAVarietiesActions.loadTGAVarietyPropertiesSuccess({ varietyProperties })),
          catchError((error) => {
            const errorMessage = this.localeService.translate('tgaVarieties.loadError');
            return of(
              UiNotificationActions.errorSnackbar({ message: errorMessage }),
              TGAVarietiesActions.loadTGAVarietyPropertiesFailure({ error: error.message }),
            );
          }),
        ),
      ),
    ),
  );

  loadAllCropVarieties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TGAVarietiesActions.loadCropVarietiesRequest),
      switchMap(() =>
        this.tgaVarietiesService.getAllCropVarieties().pipe(
          map((cropVarieties) => TGAVarietiesActions.loadCropVarietiesSuccess({ cropVarieties })),
          catchError((error) => {
            const errorMessage = this.localeService.translate('tgaVarieties.loadError');
            return of(UiNotificationActions.errorSnackbar({ message: errorMessage }), TGAVarietiesActions.loadCropVarietiesFailure({ error: error.message }));
          }),
        ),
      ),
    ),
  );

  upsertTGAVarietyProperty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TGAVarietiesActions.upsertTGAVarietyPropertyRequest),
      switchMap((action) =>
        this.tgaVarietiesService.upsertVarietyPropertiesTGA(action.varietyProperty).pipe(
          map(() => {
            const successMessage = this.localeService.translate('tgaVarieties.saveSuccess');
            return [
              UiNotificationActions.successSnackbar({ message: successMessage }),
              TGAVarietiesActions.upsertTGAVarietyPropertySuccess({ varietyProperty: action.varietyProperty }),
              TGAVarietiesActions.loadTGAVarietyPropertiesRequest(),
            ];
          }),
          switchMap((actions) => actions),
          catchError((error) => {
            const errorMessage = this.localeService.translate('tgaVarieties.saveError');
            return of(
              UiNotificationActions.errorSnackbar({ message: errorMessage }),
              TGAVarietiesActions.upsertTGAVarietyPropertyFailure({ error: error.message }),
            );
          }),
        ),
      ),
    ),
  );

  openCreateTGAVarietyPropertyDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TGAVarietiesActions.openCreateTGAVarietyPropertyDialog),
      withLatestFrom(this.store.select(selectCropVarieties), this.store.select(selectDerivedCropVarieties)),
      switchMap(([, cropVarieties, derivedCropVarieties]) => {
        const uniqueCropVarieties = cropVarieties.filter((cv) => !derivedCropVarieties.some((dcv) => dcv.ediCode === cv.ediCode));
        const dialogData: VarietyPropertyDialogData = {
          title: this.localeService.translate('tgaVarieties.addVarietyProperty'),
          cropVarieties: uniqueCropVarieties,
        };

        return this.dialog.form(VarietyPropertyDialogComponent, dialogData).pipe(
          switchMap((result) => {
            if (result?.status === DIALOG_RETURN_TYPES.confirm) {
              return of(TGAVarietiesActions.upsertTGAVarietyPropertyRequest({ varietyProperty: result.result! }));
            }
            return EMPTY;
          }),
        );
      }),
    ),
  );

  openEditTGAVarietyPropertyDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TGAVarietiesActions.openEditTGAVarietyPropertyDialog),
      withLatestFrom(this.store.select(selectCropVarieties), this.store.select(selectDerivedCropVarieties)),
      switchMap(([action, cropVarieties, derivedCropVarieties]) => {
        const uniqueCropVarieties = cropVarieties.filter(
          (cv) => !derivedCropVarieties.some((dcv) => dcv.ediCode === cv.ediCode) || action.varietyProperty.ediCode === cv.ediCode,
        );
        const dialogData: VarietyPropertyDialogData = {
          title: this.localeService.translate('tgaVarieties.editVarietyProperty'),
          varietyProperty: action.varietyProperty,
          cropVarieties: uniqueCropVarieties,
        };

        return this.dialog.form(VarietyPropertyDialogComponent, dialogData).pipe(
          switchMap((result) => {
            if (result?.status === DIALOG_RETURN_TYPES.confirm) {
              return of(TGAVarietiesActions.upsertTGAVarietyPropertyRequest({ varietyProperty: result.result! }));
            }
            return EMPTY;
          }),
        );
      }),
    ),
  );

  deleteTGAVarietyProperty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TGAVarietiesActions.deleteTGAVarietyPropertyRequest),
      switchMap((action) =>
        this.tgaVarietiesService.deleteVarietyPropertiesTGA(action.id).pipe(
          map(() => {
            const successMessage = this.localeService.translate('tgaVarieties.deleteSuccess');
            return [
              UiNotificationActions.successSnackbar({ message: successMessage }),
              TGAVarietiesActions.deleteTGAVarietyPropertySuccess(),
              TGAVarietiesActions.loadTGAVarietyPropertiesRequest(),
            ];
          }),
          switchMap((actions) => actions),
          catchError((error) => {
            const errorMessage = this.localeService.translate('tgaVarieties.deleteError');
            return of(
              UiNotificationActions.errorSnackbar({ message: errorMessage }),
              TGAVarietiesActions.deleteTGAVarietyPropertyFailure({ error: error.message }),
            );
          }),
        ),
      ),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly tgaVarietiesService: TGAVarietiesService,
    private readonly localeService: LocaleService,
    private readonly dialog: DialogService,
    private readonly store: Store,
  ) {}
}
